import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * DotGrid — the 54×54px day-button grid on Screen 5.
 *
 * State derivation:
 *   - `done`   — day appears in `completedDays`
 *   - `today`  — day equals `completedDays.length + 1`
 *   - `future` — everything else
 *
 * Only `done` and `today` are real buttons. `future` dots render as
 * presentational divs so they're not in the tab order or screen-reader
 * focus path. Out-of-order taps are still rejected at the click handler
 * (server enforces it on `today`'s position too, defensive in depth).
 *
 * The pulsing today dot is implemented with Tailwind's built-in
 * `animate-pulse` rather than a bespoke keyframe — cheaper to ship,
 * respects `prefers-reduced-motion` automatically, and visually close
 * enough to the spec's described behaviour.
 */
export interface DotGridProps {
  totalDays: number;
  completedDays: number[];
  onMarkDay: (dayNumber: number) => void;
  /** When true, all dots become non-interactive (post-completion). */
  locked?: boolean;
  className?: string;
}

type DotState = 'done' | 'today' | 'future';

function dotStateFor(day: number, completedDays: number[]): DotState {
  if (completedDays.includes(day)) return 'done';
  if (day === completedDays.length + 1) return 'today';
  return 'future';
}

export function DotGrid({
  totalDays,
  completedDays,
  onMarkDay,
  locked = false,
  className,
}: DotGridProps) {
  const days = Array.from({ length: totalDays }, (_, i) => i + 1);

  return (
    <div
      className={cn('flex flex-wrap items-center justify-center gap-2.5', className)}
      role="list"
      aria-label="Reading days"
    >
      {days.map((day) => {
        const state = dotStateFor(day, completedDays);
        return (
          <DotButton
            key={day}
            day={day}
            state={state}
            disabled={locked}
            onClick={() => onMarkDay(day)}
          />
        );
      })}
    </div>
  );
}

interface DotButtonProps {
  day: number;
  state: DotState;
  disabled: boolean;
  onClick: () => void;
}

function DotButton({ day, state, disabled, onClick }: DotButtonProps) {
  // `future` is a presentational placeholder — render as a non-interactive
  // div so screen readers don't announce it as actionable. Visually
  // grayed-out (outlined slate, no shadow) so it doesn't compete with
  // the active "today" and "done" buttons for tappability cues.
  if (state === 'future') {
    return (
      <div
        role="listitem"
        aria-label={`Day ${day} (locked)`}
        className="flex h-[54px] w-[54px] flex-col items-center justify-center rounded-full border-2 border-dashed border-or-slate-200 bg-white text-or-slate-400"
      >
        <span className="text-[15px] font-bold leading-none">{day}</span>
        <span className="mt-0.5 text-[9px] font-medium">Day {day}</span>
      </div>
    );
  }

  const isDone = state === 'done';
  return (
    <button
      type="button"
      role="listitem"
      onClick={onClick}
      disabled={disabled}
      aria-label={
        isDone
          ? `Day ${day} complete`
          : `Mark day ${day} complete`
      }
      className={cn(
        'flex h-[54px] w-[54px] flex-col items-center justify-center rounded-full text-white shadow-md transition-transform',
        'hover:scale-105 active:scale-95',
        isDone
          ? 'bg-or-green-700'
          : 'bg-or-orange-500 motion-safe:animate-pulse',
        disabled && 'cursor-not-allowed opacity-70',
      )}
    >
      {isDone ? (
        <Check className="h-5 w-5" aria-hidden />
      ) : (
        <span className="text-[15px]" aria-hidden>
          📖
        </span>
      )}
      <span className="mt-0.5 text-[9px] font-medium opacity-80">Day {day}</span>
    </button>
  );
}
